'use client'

import type { Location } from '@/lib/types/map'
import CtaDefault from './ui/cta/CtaDefault'
import Icon from './svg/icon'
import clsx from 'clsx'
import { abbreviateRegion } from '@/utils/geo/abbreviateRegion'
import { getDirectionsUrl, openDirections } from '@/utils/geo/directionsUrl'

type LocationCardProps = Location & { isDealerCard?: boolean }

export function LocationCard({
	id,
	dealer_name,
	address,
	city,
	state,
	contact_phone,
	contact_email,
	website,
	isDealerCard,
	latitude,
	longitude,
	zip,
	className,
}: LocationCardProps & { className?: string }) {
	const stateDisplay = abbreviateRegion(state)
	const cityStateZip = [city, [stateDisplay, zip].filter(Boolean).join(' ')]
		.filter(Boolean)
		.join(', ')
	const directionsUrl = getDirectionsUrl({
		latitude,
		longitude,
		address,
		city,
		state,
		zip,
		dealer_name,
	})

	return (
		<div
			className={clsx('flex flex-col gap-2 bg-white rounded-lg px-4 py-8 lg:p-10', className)}
			key={id}
		>
			{dealer_name && <h3 className="text-2xl font-medium">{dealer_name}</h3>}
			{(address || cityStateZip) && (
				<address className="mt-1 flex flex-col gap-1 not-italic">
					{address ? (
						<div className="flex items-start gap-2.5">
							<span className="flex h-[18px] w-[14px] shrink-0 justify-center pt-0.5" aria-hidden>
								<Icon icon="LocationPinSimple" className="h-[18px] w-[14px]" />
							</span>
							<span className="min-w-0 leading-snug">{address}</span>
						</div>
					) : (
						<div className="flex items-start gap-2.5">
							<span className="flex h-[18px] w-[14px] shrink-0 justify-center pt-0.5" aria-hidden>
								<Icon icon="LocationPinSimple" className="h-[18px] w-[14px]" />
							</span>
							<span className="min-w-0 leading-snug">{cityStateZip}</span>
						</div>
					)}
					{address && cityStateZip ? <div className="leading-snug">{cityStateZip}</div> : null}
				</address>
			)}
			{contact_phone && (
				<p className="mt-8">
					<span className="text-blue">Phone:</span>{' '}
					<a href={`tel:${contact_phone}`}>{contact_phone}</a>
				</p>
			)}
			{contact_email && (
				<p className="mb-4">
					<span className="text-blue">Email:</span>{' '}
					<a href={`mailto:${contact_email}`}>{contact_email}</a>
				</p>
			)}
			{website && (
				<CtaDefault
					className="w-full mt-auto"
					cta={{
						__typename: 'NavigationItemRecord',
						id,
						externalLink: website,
						label: 'Visit Website',
						useExternalLink: true,
						openInNewWindow: true,
					}}
				>
					Visit Website
				</CtaDefault>
			)}

			{isDealerCard && directionsUrl && (
				<CtaDefault
					className={`cta w-full ${!website ? 'mt-auto' : ''}`}
					onClick={() =>
						openDirections({
							latitude,
							longitude,
							address,
							city,
							state,
							zip,
							dealer_name,
						})
					}
				>
					Get Directions
				</CtaDefault>
			)}
		</div>
	)
}
